import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle, ArrowRight, AlertTriangle } from "lucide-react";
import type { QuestionGroup } from "@/types/database";

interface GroupTransitionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  completedGroup: QuestionGroup | null;
  nextGroup: QuestionGroup | null;
  onContinue: () => void;
  isLastGroup: boolean;
  isSavingResponses: boolean;
}

export const GroupTransitionDialog = React.memo(
  ({
    isOpen,
    onClose,
    completedGroup,
    nextGroup,
    onContinue,
    isLastGroup,
    isSavingResponses,
  }: GroupTransitionDialogProps) => {
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    const handleContinueClick = () => {
      if (isLastGroup) {
        setShowConfirmDialog(true);
      } else {
        onContinue();
      }
    };

    const handleConfirmSubmit = () => {
      setShowConfirmDialog(false);
      onContinue();
    };

    return (
      <>
        <Dialog open={isOpen} onOpenChange={onClose}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                Section Completed!
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              {completedGroup && (
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <h3 className="font-medium text-green-800">
                    {completedGroup.name}
                  </h3>
                  {completedGroup.description && (
                    <p className="text-sm text-green-600 mt-1">
                      {completedGroup.description}
                    </p>
                  )}
                  <p className="text-sm text-green-600 mt-2">
                    ✅ All questions completed
                  </p>
                </div>
              )}

              {!isLastGroup && nextGroup && (
                <div className="flex items-center gap-3">
                  <ArrowRight className="h-4 w-4 text-gray-400" />
                  <div className="flex-1">
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <h3 className="font-medium text-blue-800">
                        Next: {nextGroup.name}
                      </h3>
                      {nextGroup.description && (
                        <p className="text-sm text-blue-600 mt-1">
                          {nextGroup.description}
                        </p>
                      )}
                      <p className="text-sm text-blue-600 mt-2">
                        {nextGroup.questions_per_test} questions to complete
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {isLastGroup && (
                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <h3 className="font-medium text-yellow-800">Ready to Submit</h3>
                  <p className="text-sm text-yellow-600 mt-1">
                    You've completed all sections. Click continue to submit your
                    assessment.
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button
                onClick={handleContinueClick}
                disabled={isSavingResponses}
                className="animate-fade-in"
              >
                {isSavingResponses
                  ? "Saving..."
                  : isLastGroup
                  ? "Submit Assessment"
                  : "Continue to Next Section"}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Confirm Assessment Submission
              </AlertDialogTitle>
              <AlertDialogDescription className="space-y-3 pt-2">
                <p className="font-medium text-foreground">
                  Are you sure you want to submit your assessment?
                </p>
                <p>
                  Once submitted, you will not be able to make any changes to your answers.
                  Please review your responses before proceeding.
                </p>
                <p className="text-amber-600 font-medium">
                  This action cannot be undone.
                </p>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isSavingResponses}>
                Review Answers
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmSubmit}
                disabled={isSavingResponses}
                className="bg-primary hover:bg-primary/90"
              >
                {isSavingResponses ? "Saving..." : "Yes, Submit Assessment"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  }
);

GroupTransitionDialog.displayName = "GroupTransitionDialog";
